(() => {
  class AspadzLogoReviewCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryTimer = null;
      this.layoutFrame = null;
      this.logoLoopResetTimer = null;
      this.logoButtons = [];
      this.logoLoopButtons = [];
      this.logoViewport = null;
      this.logoList = null;
      this.resizeObserver = null;
      this.viewportObserver = null;
      this.hasEnteredViewport = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.handleLogoClick = this.handleLogoClick.bind(this);
      this.handleLogoLayoutChange = this.handleLogoLayoutChange.bind(this);
    }

    connectedCallback() {
      this.logoButtons = Array.from(
        this.querySelectorAll('[data-aspadz-logo-review-logo]:not([data-aspadz-logo-review-clone])')
      );
      this.logoViewport = this.querySelector('.aspadz-logo-review-carousel__logo-viewport');
      this.logoList = this.querySelector('[data-aspadz-logo-review-logos]');
      this.buildLogoLoop();
      this.logoLoopButtons.forEach((button) => button.addEventListener('click', this.handleLogoClick));
      this.addMotionListener();
      this.addLayoutListener();
      this.init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);
      window.clearTimeout(this.logoLoopResetTimer);
      window.cancelAnimationFrame(this.layoutFrame);
      this.logoLoopButtons.forEach((button) => button.removeEventListener('click', this.handleLogoClick));
      this.querySelectorAll('[data-aspadz-logo-review-clone]').forEach((button) => button.remove());
      this.logoLoopButtons = [];
      this.removeMotionListener();
      this.removeLayoutListener();

      if (this.viewportObserver) {
        this.viewportObserver.disconnect();
        this.viewportObserver = null;
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    buildLogoLoop() {
      if (!this.logoList || this.logoButtons.length === 0) return;

      if (this.logoButtons.length < 2) {
        this.logoLoopButtons = [...this.logoButtons];
        return;
      }

      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();
      const beforeButtons = this.logoButtons.map((button) => this.createLogoClone(button));
      const afterButtons = this.logoButtons.map((button) => this.createLogoClone(button));

      beforeButtons.forEach((button) => beforeFragment.appendChild(button));
      afterButtons.forEach((button) => afterFragment.appendChild(button));
      this.logoList.insertBefore(beforeFragment, this.logoButtons[0]);
      this.logoList.appendChild(afterFragment);
      this.logoLoopButtons = [...beforeButtons, ...this.logoButtons, ...afterButtons];
    }

    createLogoClone(button) {
      const clone = button.cloneNode(true);
      clone.setAttribute('data-aspadz-logo-review-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.removeAttribute('aria-current');
      clone.removeAttribute('data-shopify-editor-block');
      return clone;
    }

    addMotionListener() {
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.addListener(this.handleMotionPreferenceChange);
      }
    }

    removeMotionListener() {
      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionPreferenceChange);
      }
    }

    addLayoutListener() {
      if (typeof window.ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.handleLogoLayoutChange);
        return;
      }

      this.resizeObserver = new window.ResizeObserver(this.handleLogoLayoutChange);
      if (this.logoViewport) this.resizeObserver.observe(this.logoViewport);
      if (this.logoButtons[0]) this.resizeObserver.observe(this.logoButtons[0]);
      if (this.logoButtons.length > 1) this.resizeObserver.observe(this.logoButtons[this.logoButtons.length - 1]);
    }

    removeLayoutListener() {
      window.removeEventListener('resize', this.handleLogoLayoutChange);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }

    handleLogoLayoutChange() {
      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => {
        this.updateLogoListPadding();

        const activeButton = this.logoButtons.find((button) => button.classList.contains('is-active'));
        if (activeButton) this.centerActiveLogo(activeButton, false);
      });
    }

    init() {
      if (this.swiper) return;

      const slider = this.querySelector('[data-aspadz-logo-review-slider]');
      const slides = this.querySelectorAll('.aspadz-logo-review-carousel__slide');
      if (!slider || slides.length === 0) return;

      const delay = Number(this.dataset.autoplayDelay) || 5000;
      const initialIndex = Math.min(Math.max(Number(this.dataset.initialIndex) || 0, 0), slides.length - 1);
      const pagination = this.querySelector('[data-aspadz-logo-review-pagination]');
      this.style.setProperty('--aspadz-logo-review-delay', `${delay}ms`);
      this.updateLogoListPadding();
      this.updateActiveLogo(initialIndex, true);

      if (slides.length < 2) {
        this.handleMotionPreferenceChange();
        return;
      }

      if (typeof window.Swiper === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), 120);
        return;
      }

      this.swiper = new window.Swiper(slider, {
        allowTouchMove: true,
        autoplay: this.motionQuery.matches
          ? false
          : {
              delay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            },
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        initialSlide: initialIndex,
        loop: true,
        loopAdditionalSlides: slides.length,
        pagination: pagination
          ? {
              clickable: true,
              el: pagination,
              renderBullet(index, className) {
                return `
                  <button class="${className}" type="button" aria-label="Go to slide ${index + 1}">
                    <span class="aspadz-logo-review-carousel__pagination-track">
                      <span class="aspadz-logo-review-carousel__pagination-progress"></span>
                    </span>
                  </button>
                `;
              },
            }
          : undefined,
        slidesPerView: 1,
        speed: 550,
        watchOverflow: true,
        on: {
          init: (swiper) => {
            this.updateActiveLogo(swiper.realIndex || 0);
            this.restartProgress();
          },
          slideChange: (swiper) => {
            this.updateActiveLogo(swiper.realIndex || 0);
          },
          slideChangeTransitionStart: () => this.restartProgress(),
          autoplayResume: () => this.restartProgress(),
        },
      });

      this.observeViewportEntry();
      this.handleMotionPreferenceChange();
    }

    observeViewportEntry() {
      if (this.hasEnteredViewport || this.viewportObserver || !this.swiper) return;

      if (typeof window.IntersectionObserver === 'undefined') {
        this.handleViewportEntry();
        return;
      }

      this.viewportObserver = new window.IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.viewportObserver.disconnect();
          this.viewportObserver = null;
          this.handleViewportEntry();
        },
        { threshold: 0.01 }
      );
      this.viewportObserver.observe(this);
    }

    handleViewportEntry() {
      if (this.hasEnteredViewport || !this.swiper) return;

      this.hasEnteredViewport = true;
      this.swiper.slideNext(this.motionQuery.matches ? 0 : undefined);
      this.handleMotionPreferenceChange();
    }

    handleLogoClick(event) {
      const button = event.currentTarget;
      const index = Number(button.dataset.slideIndex);
      if (Number.isNaN(index)) return;

      if (this.swiper) {
        this.swiper.slideToLoop(index);
        this.restartAutoplay();
      } else {
        this.updateActiveLogo(index);
      }

      this.restartProgress();
    }

    updateActiveLogo(activeIndex, useOriginalButton = false) {
      this.updateLogoListPadding();

      this.logoLoopButtons.forEach((button) => {
        const isActive = Number(button.dataset.slideIndex) === activeIndex;
        button.classList.toggle('is-active', isActive);
      });

      this.logoButtons.forEach((button, index) => {
        button.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
      });

      const activeButton = useOriginalButton
        ? this.logoButtons[activeIndex]
        : this.findNearestLogoButton(activeIndex);
      if (activeButton) this.centerActiveLogo(activeButton, !useOriginalButton && !this.motionQuery.matches);
    }

    findNearestLogoButton(activeIndex) {
      if (!this.logoViewport) return this.logoButtons[activeIndex];

      const candidates = this.logoLoopButtons.filter(
        (button) => Number(button.dataset.slideIndex) === activeIndex
      );
      const viewportRect = this.logoViewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;

      return candidates.reduce((nearestButton, button) => {
        if (!nearestButton) return button;

        const buttonRect = button.getBoundingClientRect();
        const nearestRect = nearestButton.getBoundingClientRect();
        const buttonDistance = Math.abs(buttonRect.left + buttonRect.width / 2 - viewportCenter);
        const nearestDistance = Math.abs(nearestRect.left + nearestRect.width / 2 - viewportCenter);
        return buttonDistance < nearestDistance ? button : nearestButton;
      }, null);
    }

    updateLogoListPadding() {
      if (!this.logoViewport || !this.logoList || this.logoButtons.length === 0) return;

      const firstButton = this.logoButtons[0];
      const lastButton = this.logoButtons[this.logoButtons.length - 1];
      const startPadding = Math.max((this.logoViewport.clientWidth - firstButton.offsetWidth) / 2, 0);
      const endPadding = Math.max((this.logoViewport.clientWidth - lastButton.offsetWidth) / 2, 0);

      this.logoList.style.paddingLeft = `${startPadding}px`;
      this.logoList.style.paddingRight = `${endPadding}px`;
    }

    centerActiveLogo(button, animate = true) {
      if (!this.logoViewport || this.logoViewport.scrollWidth <= this.logoViewport.clientWidth + 1) return;

      const viewportRect = this.logoViewport.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const targetLeft =
        this.logoViewport.scrollLeft +
        buttonRect.left -
        viewportRect.left -
        (this.logoViewport.clientWidth - buttonRect.width) / 2;
      const maxScrollLeft = this.logoViewport.scrollWidth - this.logoViewport.clientWidth;
      const nextLeft = Math.min(Math.max(targetLeft, 0), maxScrollLeft);

      if (animate && typeof this.logoViewport.scrollTo === 'function') {
        this.logoViewport.scrollTo({ left: nextLeft, behavior: 'smooth' });
      } else {
        this.logoViewport.scrollLeft = nextLeft;
      }

      window.clearTimeout(this.logoLoopResetTimer);
      if (!button.hasAttribute('data-aspadz-logo-review-clone')) return;

      // Return to the matching item in the middle set after the repeated set finishes moving.
      this.logoLoopResetTimer = window.setTimeout(() => {
        const activeIndex = Number(button.dataset.slideIndex);
        const originalButton = this.logoButtons[activeIndex];
        if (originalButton?.classList.contains('is-active')) {
          this.centerActiveLogo(originalButton, false);
        }
      }, animate ? 650 : 0);
    }

    restartAutoplay() {
      if (!this.swiper?.autoplay || this.motionQuery.matches || !this.hasEnteredViewport) return;

      this.swiper.autoplay.stop();
      this.swiper.autoplay.start();
    }

    restartProgress() {
      this.classList.remove('is-progressing');

      window.requestAnimationFrame(() => {
        if (!this.motionQuery.matches && (this.hasEnteredViewport || this.logoButtons.length < 2)) {
          this.classList.add('is-progressing');
        }
      });
    }

    handleMotionPreferenceChange() {
      const reduceMotion = this.motionQuery.matches;
      this.classList.toggle('aspadz-logo-review-carousel--reduced-motion', reduceMotion);

      if (!this.swiper?.autoplay) {
        this.classList.toggle('is-progressing', !reduceMotion);
        return;
      }

      if (reduceMotion) {
        this.swiper.autoplay.stop();
        this.classList.remove('is-progressing');
      } else if (!this.hasEnteredViewport) {
        this.swiper.autoplay.stop();
        this.classList.remove('is-progressing');
      } else {
        this.swiper.autoplay.start();
        this.restartProgress();
      }
    }
  }

  if (!window.customElements.get('aspadz-logo-review-carousel')) {
    window.customElements.define('aspadz-logo-review-carousel', AspadzLogoReviewCarousel);
  }
})();
